/**
 * A response that did not match the schema the panel expected.
 *
 * This is news about US, not about the server: the backend answered, and its
 * answer is the contract. So a drift is always reported even when the request
 * is allowed to continue — the alternative is a panel that quietly parses less
 * and less of what it receives until a screen goes blank for reasons nobody
 * can reconstruct.
 */
export interface ContractDrift {
  path: string;
  method: string;
  status: number;
  /** Zod issues flattened to `path: message`, in the order zod reported them. */
  issues: string[];
  /** Whether the caller got the raw body anyway (a write) or an ApiError (a read). */
  degraded: boolean;
}

export type ContractDriftReporter = (drift: ContractDrift) => void;

const defaultReporter: ContractDriftReporter = (drift) => {
  console.error(
    `[api] contract drift ${drift.method} ${drift.path} (${drift.status})`,
    drift.issues,
  );
};

let reporter: ContractDriftReporter = defaultReporter;

/** Swap the sink — Sentry in the browser, an array in a test. */
export function setContractDriftReporter(fn: ContractDriftReporter): void {
  reporter = fn;
}

export function resetContractDriftReporter(): void {
  reporter = defaultReporter;
}

export function reportContractDrift(drift: ContractDrift): void {
  // A broken reporter must never turn a working request into a failed one.
  try {
    reporter(drift);
  } catch {
    /* empty */
  }
}
